/**
 * API Endpoints Configuration
 *
 * Central configuration for all API endpoints with EXPLICIT gateway assignment.
 * Each endpoint includes its full base URL - no runtime routing logic.
 *
 * IMPORTANT: These paths MUST match the deployed backend in template-core.yaml and template-agents.yaml
 *
 * Gateway Architecture (from .myworld.json):
 * - API_BASE_CORE: Core business operations (template-core.yaml) - 126 handlers
 * - API_BASE_AGENTS: Agent operations (template-agents.yaml) - 72 handlers
 *
 * Naming conventions:
 * - /tim/* = Tim-Combo standard CRUD endpoints (core business entities)
 * - /api/* = Specialized endpoints (agents, billing, superadmin, etc.)
 * - /auth/* = Authentication endpoints
 *
 * Standards: .equilateral-standards/frontend-development/api_client_standards.md
 */

// =============================================================================
// GATEWAY CONFIGURATION
// =============================================================================

// Core Gateway: Business operations, CRUD, billing, admin (template-core.yaml)
const API_BASE_CORE = process.env.REACT_APP_API_URL_CORE || process.env.REACT_APP_API_URL || '';

// Agents Gateway: Agent execution, topology, workflows (template-agents.yaml)
const API_BASE_AGENTS = process.env.REACT_APP_API_URL_AGENTS || process.env.REACT_APP_API_URL || '';

// Enhancements Gateway: Security, tracing, governance, compliance (template-enhancements.yaml)
const API_BASE_ENHANCEMENTS = process.env.REACT_APP_API_URL_ENHANCEMENTS || process.env.REACT_APP_API_URL || '';

// P3 Gateway: Analytics, memory, inference, governance hardening (template-p3.yaml)
const API_BASE_P3 = process.env.REACT_APP_API_URL_P3 || process.env.REACT_APP_API_URL || '';

// Validate configuration in production
if (process.env.NODE_ENV === 'production') {
  if (!API_BASE_CORE) {
    console.error('[endpoints] API_BASE_CORE not configured - API calls will fail');
  }
  if (API_BASE_CORE === API_BASE_AGENTS) {
    console.warn('[endpoints] API_BASE_AGENTS not configured - routing agents to core gateway');
  }
}

// =============================================================================
// STABLE WEBHOOK URL - For external partner integrations (GitHub, etc.)
// Uses custom domain to isolate partners from internal API Gateway changes
// =============================================================================
const WEBHOOK_BASE_STABLE = process.env.REACT_APP_WEBHOOK_URL || 'https://api.dev.equilateral.ai';

// =============================================================================
// ENDPOINT DEFINITIONS - All endpoints include explicit gateway assignment
// =============================================================================

export const API_ENDPOINTS = {
  // ============================================================================
  // AUTHENTICATION → Core Gateway
  // ============================================================================
  AUTH: {
    ME: API_BASE_CORE + '/api/auth/authMe'  // Returns current user profile from ID token (POST)
  },

  // ============================================================================
  // TIM-COMBO CORE ENTITIES (CRUD pattern) → Core Gateway
  // ============================================================================

  // Client Management - via /api/clients/* endpoints (template-core.yaml)
  CLIENTS: {
    LIST: API_BASE_CORE + '/api/clients/clientsGet',              // GET - list clients
    GET: API_BASE_CORE + '/api/clients/clientGet',                // GET - get single client
    CREATE: API_BASE_CORE + '/api/clients/clientCreate',          // POST - create client
    UPDATE: API_BASE_CORE + '/api/clients/clientUpdate',          // PUT - update client
    DELETE: API_BASE_CORE + '/api/clients/clientDelete',          // DELETE - delete client
    COMPANIES: API_BASE_CORE + '/api/clients/clientCompaniesGet', // GET - client's companies
    COST_SUMMARY: API_BASE_CORE + '/api/clients/clientCostSummaryGet', // GET - client cost summary
    STACK_CONFIG_GET: API_BASE_CORE + '/api/clients/clientStackConfigurationGet',     // GET - stack config
    STACK_CONFIG_CREATE: API_BASE_CORE + '/api/clients/clientStackConfigurationCreate' // POST - create stack config
  },

  // Company Management - via /api/company/* endpoints (template-core.yaml)
  COMPANIES: {
    LIST: API_BASE_CORE + '/api/company/companiesGet',        // GET - list companies
    GET: API_BASE_CORE + '/api/company/companiesGet',         // GET with Company_ID param (same endpoint)
    CREATE: API_BASE_CORE + '/api/company/companyCreate',     // POST
    UPDATE: API_BASE_CORE + '/api/company/companyUpdate',     // PUT
    DELETE: API_BASE_CORE + '/api/company/companyDelete',     // DELETE
    COST_SUMMARY: API_BASE_CORE + '/api/company/companyCostSummaryGet' // GET - company cost summary
  },

  // Project Management - via /api/projects/* endpoints (template-core.yaml)
  PROJECTS: {
    LIST: API_BASE_CORE + '/api/projects/projectsGet',                // GET - list projects
    GET: API_BASE_CORE + '/api/projects/projectGet',                  // GET - single project
    CREATE: API_BASE_CORE + '/api/projects/projectCreate',            // POST
    UPDATE: API_BASE_CORE + '/api/projects/projectUpdate',            // PUT
    DELETE: API_BASE_CORE + '/api/projects/projectDelete',            // DELETE
    STANDARDS: API_BASE_CORE + '/api/projects/projectStandardsGet',   // GET - project standards
    STANDARDS_UPDATE: API_BASE_CORE + '/api/projects/projectStandardsUpdate', // PUT - update standards
    TEAM: API_BASE_CORE + '/api/projects/projectTeamMemberList',      // GET - team list
    TEAM_ADD: API_BASE_CORE + '/api/projects/projectTeamMemberAdd',   // POST - add team member
    TEAM_UPDATE: API_BASE_CORE + '/api/projects/projectTeamMemberUpdate', // PUT - update team member
    TEAM_REMOVE: API_BASE_CORE + '/api/projects/projectTeamMemberRemove', // DELETE - remove team member
    BUDGET: API_BASE_CORE + '/api/projects/projectBudgetGet',         // GET - project budget
    BUDGET_UPDATE: API_BASE_CORE + '/api/projects/projectBudgetUpdate', // PUT - update budget
    COSTS: API_BASE_CORE + '/api/projects/projectCostsGet',           // GET - project costs
    COST_SUMMARY: API_BASE_CORE + '/api/projects/projectCostSummaryGet', // GET - cost summary
    REPOSITORY: API_BASE_CORE + '/api/projects/projectRepositoryGet'  // GET - project repository
  },

  // Card Management - via /api/cards/* (template-core.yaml)
  CARDS: {
    LIST: API_BASE_CORE + '/api/cards/cardsGet',          // GET - list cards
    GET: API_BASE_CORE + '/api/cards/cardGet',            // GET single card
    CREATE: API_BASE_CORE + '/api/cards/cardCreate',      // POST
    UPDATE: API_BASE_CORE + '/api/cards/cardUpdate',      // PUT
    DELETE: API_BASE_CORE + '/api/cards/cardDelete'       // DELETE
  },

  // EA-Agile specific card operations - via /api/utilities/* (template-core.yaml)
  EA_AGILE: {
    CARDS: API_BASE_CORE + '/api/cards/cardsGet',                        // Same as CARDS.LIST
    CARD_DOCUMENT_GET: API_BASE_CORE + '/api/utilities/eaAgileCardDocumentGet',
    CARD_DOCUMENT_PUT: API_BASE_CORE + '/api/utilities/eaAgileCardDocumentPut',
    CARD_DOCUMENT_ASSIST: API_BASE_CORE + '/api/utilities/eaAgileDocumentAssistPost',
    // Review comments - uses card document endpoints with review data stored in card metadata
    CARD_COMMENTS_GET: API_BASE_CORE + '/api/utilities/eaAgileCardDocumentGet',   // GET comments via card doc
    CARD_COMMENTS_POST: API_BASE_CORE + '/api/utilities/eaAgileCardDocumentPut'   // POST comments via card doc update
  },

  // Brand Management - via /api/brands/* endpoints (template-core.yaml)
  BRANDS: {
    LIST: API_BASE_CORE + '/api/brands/brandsGet',        // GET - list brands
    GET: API_BASE_CORE + '/api/brands/brandGet',          // GET single brand
    CREATE: API_BASE_CORE + '/api/brands/brandCreate',    // POST
    UPDATE: API_BASE_CORE + '/api/brands/brandUpdate',    // PUT
    CONFIG: API_BASE_CORE + '/api/brands/brandConfigGet',  // GET brand config
    WAITLIST: API_BASE_CORE + '/api/brands/waitlist'       // POST - public waitlist signup (no auth)
  },

  // Standards Management
  STANDARDS: {
    LIST: API_BASE_CORE + '/tim/standards',
    CATEGORIES: API_BASE_CORE + '/tim/standards/categories',
    SEARCH: API_BASE_CORE + '/tim/standards/search',
    CONTENT: API_BASE_CORE + '/tim/standard/content',
    // S3-based content storage (presigned URLs) - routed to AGENTS gateway
    CONTENT_GET: API_BASE_AGENTS + '/tim/standardContent',     // GET ?specId=xxx - presigned URL for S3 read
    CONTENT_PUT: API_BASE_AGENTS + '/tim/standardContent'      // POST ?specId=xxx - presigned URL for S3 write
  },

  // Specification Management - endpoints match template-core.yaml /api/specifications/*
  SPECIFICATIONS: {
    LIST: API_BASE_CORE + '/api/specifications/specificationsGet',       // GET - list specs
    GET: API_BASE_CORE + '/api/specifications/specificationGet',         // GET single spec
    CREATE: API_BASE_CORE + '/api/specifications/specificationCreate',   // POST
    UPDATE: API_BASE_CORE + '/api/specifications/specificationUpdate',   // PUT
    DELETE: API_BASE_CORE + '/api/specifications/specificationDelete',   // DELETE
    TEMPLATES: API_BASE_CORE + '/api/specifications/specificationTemplatesGet',  // GET templates
    TEMPLATE_CREATE: API_BASE_CORE + '/api/specifications/specificationTemplateCreate',  // POST - create custom template
    TEMPLATE_UPDATE: API_BASE_CORE + '/api/specifications/specificationTemplateUpdate',  // PUT - update custom template
    EMBELLISH: API_BASE_CORE + '/api/specifications/specificationEmbellishPost',  // POST - AI embellishment
    GENERATE_FROM_CODEBASE: API_BASE_CORE + '/api/specifications/specificationGenerateFromCodebase', // POST - generate specs from codebase scan
    APPROVAL: API_BASE_CORE + '/api/specifications/specificationApprovalSubmit',  // POST approval
    APPROVAL_STATUS: API_BASE_CORE + '/api/specifications/specificationApprovalStatus',  // GET approval status
    REQUEST_REVIEW: API_BASE_CORE + '/api/specifications/specificationReviewRequest',    // POST request review
    REPOSITORY: API_BASE_CORE + '/api/specifications/specificationRepositoryLinkGet',    // GET repository link
    REPOSITORY_LINK: API_BASE_CORE + '/api/specifications/specificationRepositoryLinkPost', // POST repository link
    // PowerSpec CPO Dashboard endpoints
    COSTS: API_BASE_CORE + '/api/specifications/specificationCostsGet',           // GET - execution costs for a spec
    COST_SUMMARY: API_BASE_CORE + '/api/specifications/specificationCostSummaryGet', // GET - CPO metrics
    METRICS: API_BASE_CORE + '/api/specifications/specificationMetricsGet',        // GET - dashboard metrics
    RECOMMENDATIONS: API_BASE_CORE + '/api/specifications/specificationRecommendationsGet', // GET - AI recommendations
    RECOMMENDATION_ACKNOWLEDGE: API_BASE_CORE + '/api/specifications/specificationRecommendationAcknowledge', // PUT - acknowledge recommendation
    // Version History
    VERSION_CREATE: API_BASE_CORE + '/api/specifications/specificationVersionCreatePost', // POST - create version snapshot
    // Real-time drift notifications (polling fallback)
    NOTIFICATIONS: API_BASE_CORE + '/api/specifications/specificationNotificationsGet', // GET - unread notifications
    // Dependency Matrix
    DEPENDENCIES_GET: API_BASE_CORE + '/api/specifications/codeDependenciesGet',          // GET - code dependencies
    DEPENDENCY_CREATE: API_BASE_CORE + '/api/specifications/codeDependencyCreatePost',    // POST - create dependency
    // S3-based content storage (presigned URLs) - routed to AGENTS gateway
    CONTENT_GET: API_BASE_AGENTS + '/tim/specificationContent',     // GET ?specId=xxx - presigned URL for S3 read
    CONTENT_PUT: API_BASE_AGENTS + '/tim/specificationContent',     // POST ?specId=xxx - presigned URL for S3 write
    // Bulk Import (onboarding)
    IMPORT: API_BASE_P3 + '/api/specifications/specificationImportPost',  // POST - bulk import specs
    // Data Export
    EXPORT: API_BASE_P3 + '/api/specifications/specificationExportGet'   // GET ?Client_ID=&format=csv|json
  },

  // User Management - via /api/users/* endpoints (template-core.yaml)
  USERS: {
    LIST: API_BASE_CORE + '/api/users/userGet',           // No usersGet endpoint - use userGet with query params
    GET: API_BASE_CORE + '/api/users/userGet',
    CREATE: API_BASE_CORE + '/api/users/userCreate',
    UPDATE: API_BASE_CORE + '/api/users/userUpdate',
    DELETE: API_BASE_CORE + '/api/users/userDelete',
    ENTITLEMENTS: API_BASE_CORE + '/api/users/userEntitlementsGet',
    ENTITLEMENT_CREATE: API_BASE_CORE + '/api/users/userEntitlementCreate',
    ENTITLEMENT_DELETE: API_BASE_CORE + '/api/users/userEntitlementDelete'
  },

  // Entitlements (RBAC)
  ENTITLEMENTS: {
    LIST: API_BASE_CORE + '/tim/entitlements',
    GET: API_BASE_CORE + '/tim/entitlements',
    CREATE: API_BASE_CORE + '/tim/entitlements',
    UPDATE: API_BASE_CORE + '/tim/entitlements',
    DELETE: API_BASE_CORE + '/tim/entitlements'
  },

  // Team Management
  TEAM: {
    INVITE: API_BASE_CORE + '/tim/team/invite'
  },

  // ============================================================================
  // SPECIALIZED API ENDPOINTS → Core Gateway
  // ============================================================================

  // Repository Management - via /api/repositories/* (template-core.yaml)
  REPOSITORIES: {
    LIST: API_BASE_CORE + '/api/repositories/repositoryConnectionsGet',
    GET: API_BASE_CORE + '/api/repositories/repositoryConnectionsGet',
    CREATE: API_BASE_CORE + '/api/repositories/repositoryConnectionCreate',
    DELETE: API_BASE_CORE + '/api/repositories/repositoryConnectionDelete',
    SCAN: API_BASE_CORE + '/api/repositories/repositoryScanTrigger',
    SCANS: API_BASE_CORE + '/api/repositories/repositoryScansGet',
    SCAN_FINDINGS: API_BASE_CORE + '/api/repositories/repositoryScanFindingsGet',
    CODEBASE_SCAN: API_BASE_CORE + '/api/repositories/codebaseScanTrigger'
  },

  // Codebase scanning - via /api/repositories/* (template-core.yaml)
  CODEBASE: {
    SCAN: API_BASE_CORE + '/api/repositories/codebaseScanTrigger'
  },

  // Knowledge Management → Enhancements Gateway (template-enhancements.yaml)
  KNOWLEDGE: {
    HARVEST: API_BASE_ENHANCEMENTS + '/api/knowledge/knowledgeHarvestPost',
    GENERATE: API_BASE_ENHANCEMENTS + '/api/knowledge/knowledgeGenerateDocumentPost'
  },

  // Compliance Templates → Enhancements Gateway (template-enhancements.yaml)
  COMPLIANCE: {
    TEMPLATES_LIST: API_BASE_ENHANCEMENTS + '/api/compliance/complianceTemplatesGet',
    TEMPLATE_GET: API_BASE_ENHANCEMENTS + '/api/compliance/complianceTemplateGet'
  },

  // Compliance Templates → Enhancements Gateway (template-enhancements.yaml)
  COMPLIANCE_TEMPLATES: {
    LIST: API_BASE_ENHANCEMENTS + '/api/compliance/complianceTemplatesGet',
    GET: API_BASE_ENHANCEMENTS + '/api/compliance/complianceTemplateGet'
  },

  // Document Management
  DOCUMENTS: {
    LIST: API_BASE_CORE + '/api/documents',
    GET: API_BASE_CORE + '/api/documents',
    CREATE: API_BASE_CORE + '/api/documents',
    UPDATE: API_BASE_CORE + '/api/documents',
    DELETE: API_BASE_CORE + '/api/documents'
  },

  // Standard Review/Approval - via /api/standards/* (template-core.yaml)
  STANDARD_REVIEW: {
    REQUEST_REVIEW: API_BASE_CORE + '/api/standards/standardReviewRequest',  // POST - request review
    UPDATE: API_BASE_CORE + '/api/standards/standardReviewUpdate',           // PUT - update review decision
    PENDING: API_BASE_CORE + '/api/standards/standardReviewsPending'         // GET - pending reviews
  },

  // Standards Profiles Management - via /api/standards/* (template-core.yaml)
  // Profiles tune delivery PRIORITY, not applicability (migration 037)
  STANDARDS_PROFILES: {
    LIST: API_BASE_CORE + '/api/standards/profilesGet',            // GET ?Client_ID= - list profiles
    GET: API_BASE_CORE + '/api/standards/profileGet',              // GET ?profile_id= - single profile
    CREATE: API_BASE_CORE + '/api/standards/profileCreate',        // POST - create profile
    UPDATE: API_BASE_CORE + '/api/standards/profileUpdate',        // PUT - update profile
    DELETE: API_BASE_CORE + '/api/standards/profileDelete',        // DELETE - delete profile
    PREVIEW: API_BASE_CORE + '/api/standards/profilePreview'       // GET ?profile_id= - preview delivery impact
  },

  // Incident Management
  INCIDENT: {
    GENERATE_STANDARD: API_BASE_CORE + '/api/incident/generate-standard'
  },

  // Approval Groups
  APPROVAL_GROUPS: {
    LIST: API_BASE_CORE + '/tim/approvalgroup',
    CREATE: API_BASE_CORE + '/tim/approvalgroup',
    UPDATE: API_BASE_CORE + '/tim/approvalgroup',
    DELETE: API_BASE_CORE + '/tim/approvalgroup'
  },

  // ============================================================================
  // WORKFLOW INSPECTOR → Core Gateway (template-core.yaml /api/workflow-inspector/*)
  // Read-only observability for completed workflow executions
  // ============================================================================
  WORKFLOW_INSPECTOR: {
    EXECUTIONS_LIST: API_BASE_ENHANCEMENTS + '/api/workflow-inspector/executionsListGet',      // GET ?limit=&offset=&status=
    EXECUTION_DETAIL: API_BASE_ENHANCEMENTS + '/api/workflow-inspector/executionDetailGet'     // GET ?execution_id=
  },

  // ============================================================================
  // AGENTS → Agents Gateway (template-agents.yaml - separate API Gateway)
  // ============================================================================
  AGENTS: {
    EXECUTE: API_BASE_AGENTS + '/api/agents/execute',
    EXECUTION_STATUS: API_BASE_AGENTS + '/api/agents/execution',
    AUDIT_TRAIL: API_BASE_AGENTS + '/api/agents/audit-trail',
    METHODS: API_BASE_AGENTS + '/api/agents/methods',
    TOPOLOGY: API_BASE_AGENTS + '/api/agents/topology',
    CAPABILITY_PACKS: API_BASE_AGENTS + '/api/agents/capability-packs',
    WORKFLOW_PALETTE: API_BASE_AGENTS + '/api/agents/workflow-palette',
    AB_RESULTS: API_BASE_AGENTS + '/api/agents/ab-results',
    COST_ACCURACY: API_BASE_AGENTS + '/api/agents/cost-accuracy',
    HEALTH_TREND: API_BASE_AGENTS + '/api/agents/health-trend',
    TRACES_GET: API_BASE_AGENTS + '/api/agents/traces/get',             // GET ?traceId=
    TRACES_LIST: API_BASE_AGENTS + '/api/agents/traces',                // GET ?limit=&offset=&status=&rootAgent=
    TRACES_SEARCH: API_BASE_AGENTS + '/api/agents/traces/search',       // POST { query, filters }
    NL_PARSE: API_BASE_AGENTS + '/api/agents/workflow-nl-parse'          // POST { description, availableAgents }
  },

  // ============================================================================
  // SECURITY POSTURE → Enhancements Gateway
  // ============================================================================
  SECURITY_POSTURE: {
    CONSOLIDATED: API_BASE_ENHANCEMENTS + '/api/governance/security-posture',  // GET - composite security score per agent
    EMERGENCY_OVERRIDES: API_BASE_ENHANCEMENTS + '/api/governance/emergency-overrides/get',  // GET
    ANOMALIES: API_BASE_ENHANCEMENTS + '/api/governance/anomalies/get',                      // GET
    CIRCUIT_BREAKER_STATES: API_BASE_AGENTS + '/api/agents/circuit-breaker/states'            // GET
  },

  // ============================================================================
  // BILLING & PAYMENTS → Core Gateway
  // ============================================================================
  BILLING: {
    CHECKOUT_SESSION: API_BASE_CORE + '/api/billing/checkout-session',
    CUSTOMER_PORTAL: API_BASE_CORE + '/api/billing/customer-portal',
    PAYMENT_METHODS: API_BASE_CORE + '/api/billing/payment-methods',
    PAYMENT_METHODS_DEFAULT: API_BASE_CORE + '/api/billing/payment-methods/default',
    STRIPE_WEBHOOK: API_BASE_CORE + '/api/billing/stripe-webhook',
    // Data Export
    COST_REPORT_EXPORT: API_BASE_P3 + '/api/billing/costReportExportGet',  // GET ?Client_ID=&period_start=&period_end=&format=csv|json
    // Pending backend implementation:
    SUBSCRIPTION: API_BASE_CORE + '/api/billing/subscription',
    USAGE: API_BASE_CORE + '/api/billing/usage',
    INVOICES: API_BASE_CORE + '/api/billing/invoices',
    PLANS: API_BASE_CORE + '/api/billing/plans',
    CANCEL: API_BASE_CORE + '/api/billing/cancel'
  },

  // ============================================================================
  // ORGANIZATIONS → Core Gateway (pending backend implementation)
  // ============================================================================
  ORGANIZATIONS: {
    LIST: API_BASE_CORE + '/api/organizations',
    GET: API_BASE_CORE + '/api/organizations',
    CREATE: API_BASE_CORE + '/api/organizations',
    UPDATE: API_BASE_CORE + '/api/organizations',
    DELETE: API_BASE_CORE + '/api/organizations'
  },

  // ============================================================================
  // COSTS → Core Gateway
  // ============================================================================
  COSTS: {
    CLIENTS: API_BASE_CORE + '/api/costs/clients',
    COMPANIES: API_BASE_CORE + '/api/costs/companies',
    PROJECTS: API_BASE_CORE + '/api/costs/projects'
  },

  // ============================================================================
  // SUPERADMIN → Core Gateway (template-core.yaml /api/superadmin/*)
  // ============================================================================
  SUPERADMIN: {
    PLATFORM_STATS: API_BASE_CORE + '/api/superadmin/superadminPlatformStatsGet',
    BRANDS_STATS: API_BASE_CORE + '/api/superadmin/superadminBrandStatsGet',
    CLIENTS: API_BASE_CORE + '/api/superadmin/superadminClientsGet',
    CLIENT_UPDATE: API_BASE_CORE + '/api/superadmin/superadminClientUpdate',
    CLIENT_ONBOARD: API_BASE_CORE + '/api/superadmin/superadminClientOnboardingPost',
    CLIENT_SUSPEND: API_BASE_CORE + '/api/superadmin/superadminClientSuspend',
    CLIENT_REACTIVATE: API_BASE_CORE + '/api/superadmin/superadminClientReactivate',
    USERS: API_BASE_CORE + '/api/superadmin/superadminUsersGet',
    USER_UPDATE: API_BASE_CORE + '/api/superadmin/superadminUserUpdate',
    AUDIT: API_BASE_CORE + '/api/superadmin/superadminAuditLogGet',
    IMPERSONATE: API_BASE_CORE + '/api/superadmin/superadminImpersonate',
    SSO_TEST: API_BASE_CORE + '/api/sso/ssoConnectionTest'
  },

  // ============================================================================
  // ONBOARDING → Core Gateway (self-service onboarding, no superadmin required)
  // ============================================================================
  ONBOARDING: {
    SELF_SERVICE: API_BASE_CORE + '/api/onboarding/self-service',
    PROGRESS: API_BASE_CORE + '/api/onboarding/progress',         // GET ?Client_ID= - get onboarding progress
    STEP_UPDATE: API_BASE_CORE + '/api/onboarding/step',          // PUT {Client_ID, step_id, is_completed}
    RESET: API_BASE_CORE + '/api/onboarding/reset'                // POST {Client_ID} - reset progress (admin)
  },

  // ============================================================================
  // ADMIN → Core Gateway (maps to /api/clients/* and /api/users/*)
  // ============================================================================
  ADMIN: {
    CLIENTS: API_BASE_CORE + '/api/clients/clientsGet',
    CLIENT: API_BASE_CORE + '/api/clients/clientGet',
    USER: API_BASE_CORE + '/api/users/userGet',
    USER_ENTITLEMENTS: API_BASE_CORE + '/api/users/userEntitlementsGet',
    USER_ENTITLEMENTS_UPDATE: API_BASE_CORE + '/api/users/userEntitlementsUpdate',
    UPSELL_DETECT: API_BASE_CORE + '/api/utilities/upsellDetection',
    UPSELL_STATUS: API_BASE_CORE + '/api/utilities/upsellStatusGet',
    UPSELL_TRIGGER: API_BASE_CORE + '/api/utilities/upsellCampaignTrigger'
  },

  // ============================================================================
  // CREDENTIALS (BYOL) → Core Gateway (template-core.yaml /api/byol/*)
  // ============================================================================
  CREDENTIALS: {
    BYOL_GET: API_BASE_CORE + '/api/byol/byolCredentialsGet',
    BYOL_POST: API_BASE_CORE + '/api/byol/byolCredentialPost',
    BYOL_PUT: API_BASE_CORE + '/api/byol/byolCredentialPut',
    BYOL_DELETE: API_BASE_CORE + '/api/byol/byolCredentialDelete',
    BYOL_MODELS: API_BASE_CORE + '/api/byol/byolModelsGet'
  },

  // ============================================================================
  // MARKETPLACE → Core Gateway (template-core.yaml /api/marketplace/*)
  // ============================================================================
  MARKETPLACE: {
    ENTITLEMENT: API_BASE_CORE + '/api/marketplace/marketplaceEntitlement',
    SUBSCRIBE: API_BASE_CORE + '/api/marketplace/marketplaceSubscribe',
    UNSUBSCRIBE: API_BASE_CORE + '/api/marketplace/marketplaceUnsubscribe'
  },

  // ============================================================================
  // GITHUB APP → Core Gateway (template-core.yaml /api/github-app/*)
  // Platform Identity for repository operations
  // ============================================================================
  GITHUB_APP: {
    // Webhook endpoint - uses STABLE URL for GitHub App configuration (no auth - uses HMAC signature)
    WEBHOOK: WEBHOOK_BASE_STABLE + '/api/github-app/installations/webhook',
    // Installation management (requires Cognito auth, use query params per API standards)
    INSTALLATIONS_LIST: API_BASE_CORE + '/api/github-app/installations',  // GET ?Client_ID=
    INSTALLATION_GET: API_BASE_CORE + '/api/github-app/installations',    // GET ?installation_id=
    INSTALLATION_REPOS: API_BASE_CORE + '/api/github-app/installations/repos',  // GET ?installation_id=
    INSTALLATION_UPDATE: API_BASE_CORE + '/api/github-app/installations', // PUT {Installation_ID, Auto_Approval_Level, ...}
    INSTALLATION_DELETE: API_BASE_CORE + '/api/github-app/installations', // DELETE {Installation_ID}
    // Public assessment endpoint (no auth - for GitHub Issue links)
    ASSESSMENTS: API_BASE_CORE + '/api/github-app/assessments',  // GET ?repository= (PUBLIC)
    // Authenticated assessment list (requires auth)
    ASSESSMENTS_LIST: API_BASE_CORE + '/api/github-app/assessments/list'  // GET ?Client_ID= (AUTH)
  },

  // ============================================================================
  // GITHUB MARKETPLACE → Stable URL for GitHub Marketplace App configuration
  // GitHub Marketplace subscription events
  // ============================================================================
  GITHUB_MARKETPLACE: {
    WEBHOOK: WEBHOOK_BASE_STABLE + '/api/github-marketplace/webhook'  // POST (no auth - uses HMAC signature)
  },

  // ============================================================================
  // PLATFORM COMMITS → Core Gateway (template-core.yaml /api/platform-commits/*)
  // Approval workflow for platform repository commits
  // Per API standards: No path parameters - use body for POST, query for GET
  // ============================================================================
  PLATFORM_COMMITS: {
    LIST: API_BASE_CORE + '/api/platform-commits',             // GET ?Client_ID= - list commits for audit trail
    CREATE: API_BASE_CORE + '/api/platform-commits',           // POST {body} - create pending commit
    APPROVE: API_BASE_CORE + '/api/platform-commits/approve',  // POST {commit_id, comment} - approve pending commit
    REJECT: API_BASE_CORE + '/api/platform-commits/reject'     // POST {commit_id, reason} - reject pending commit
  },

  // ============================================================================
  // WORKFLOW APPROVALS → Core Gateway (template-core.yaml /api/workflow-approvals/*)
  // Consequence Tier Gate hold queue — workflow steps awaiting human approval
  // Per API standards: No path parameters - use body for POST, query for GET
  // ============================================================================
  WORKFLOW_APPROVALS: {
    LIST: API_BASE_ENHANCEMENTS + '/api/workflow-approvals',              // GET ?status=&Client_ID= - list pending approvals
    DETAIL: API_BASE_ENHANCEMENTS + '/api/workflow-approvals/detail',     // GET ?hold_id= - full hold details
    APPROVE: API_BASE_ENHANCEMENTS + '/api/workflow-approvals/approve',   // POST {hold_id, comment} - approve held step
    REJECT: API_BASE_ENHANCEMENTS + '/api/workflow-approvals/reject',     // POST {hold_id, reason} - reject held step
    RESUME: API_BASE_ENHANCEMENTS + '/api/workflow-approvals/resume'      // POST {hold_id} - trigger step execution after approval
  },

  // ============================================================================
  // BRAND-SPECIFIC METRICS → Core Gateway (pending backend implementation)
  // ============================================================================
  VELOCITYMIND: {
    METRICS: API_BASE_CORE + '/api/velocitymind/metrics'
  },
  AGENCYFLOW: {
    METRICS: API_BASE_CORE + '/api/agencyflow/metrics'
  },

  // ============================================================================
  // COLLABORATION → Core Gateway (template-core.yaml /api/utilities/*)
  // Active collaborative editing session monitoring
  // ============================================================================
  COLLABORATION: {
    SESSIONS: API_BASE_AGENTS + '/api/agents/collaborativeSessionsGet'  // GET ?Client_ID= - active sessions
  },

  // ============================================================================
  // GOVERNANCE → Enhancements Gateway (template-enhancements.yaml /api/governance/*)
  // Cross-entity dispatch governance (EQ-PROV-007)
  // ============================================================================
  GOVERNANCE: {
    // Cross-Entity Dispatch Policies
    CROSS_ENTITY_POLICY_CREATE: API_BASE_ENHANCEMENTS + '/api/governance/cross-entity/policies',      // POST
    CROSS_ENTITY_POLICIES_GET: API_BASE_ENHANCEMENTS + '/api/governance/cross-entity/policies',        // GET ?Client_ID=
    CROSS_ENTITY_POLICY_UPDATE: API_BASE_ENHANCEMENTS + '/api/governance/cross-entity/policies',       // PUT
    // Cross-Entity Touchpoints
    CROSS_ENTITY_TOUCHPOINT_CREATE: API_BASE_ENHANCEMENTS + '/api/governance/cross-entity/touchpoints', // POST
    CROSS_ENTITY_TOUCHPOINTS_GET: API_BASE_ENHANCEMENTS + '/api/governance/cross-entity/touchpoints',   // GET ?Client_ID=
    CROSS_ENTITY_TOUCHPOINT_UPDATE: API_BASE_ENHANCEMENTS + '/api/governance/cross-entity/touchpoints', // PUT
    // Cross-Entity Audit
    CROSS_ENTITY_AUDIT_GET: API_BASE_ENHANCEMENTS + '/api/governance/cross-entity/audit',               // GET ?Client_ID=
    CROSS_ENTITY_AUDIT_SUMMARY: API_BASE_ENHANCEMENTS + '/api/governance/cross-entity/audit/summary',   // GET ?Client_ID=
    // Cross-Entity Topology
    CROSS_ENTITY_TOPOLOGY: API_BASE_ENHANCEMENTS + '/api/governance/cross-entity/topology'              // GET ?Client_ID=
  },

  // ============================================================================
  // INTERNAL → Core Gateway (template-core.yaml /api/usage/*)
  // ============================================================================
  INTERNAL: {
    USAGE_RECORD: API_BASE_CORE + '/api/usage/usageRecordPost'
  },

  // ============================================================================
  // P3 GOVERNANCE → P3 Gateway (template-p3.yaml /api/governance/*)
  // Consequence Tier Gating, Earned Authority Lifecycle, FI-Authority, Intent Capsules
  // ============================================================================
  P3_GOVERNANCE: {
    // Consequence Tier Gating
    TIER_GATE_EVALUATE: API_BASE_P3 + '/api/governance/tier-gate/evaluate',        // POST - evaluate gate request
    TIER_GATE_HISTORY: API_BASE_P3 + '/api/governance/tier-gate/history',           // GET - gate decision history
    TIER_GATE_CONFIG_GET: API_BASE_P3 + '/api/governance/tier-gate/config',         // GET - tier config
    TIER_GATE_CONFIG_PUT: API_BASE_P3 + '/api/governance/tier-gate/config',         // PUT - update tier config
    // Earned Authority Lifecycle
    AUTHORITY_LIFECYCLE: API_BASE_P3 + '/api/governance/authority-lifecycle',                 // GET - lifecycle summary
    AUTHORITY_PROMOTION_HISTORY: API_BASE_P3 + '/api/governance/authority-lifecycle/promotions', // GET - promotion events
    AUTHORITY_DEMOTION_TRIGGER: API_BASE_P3 + '/api/governance/authority-lifecycle/demote',   // POST - trigger demotion
    // FI-to-Authority Wiring
    FI_AUTHORITY_MAPPING: API_BASE_P3 + '/api/governance/fi-authority/mapping',     // GET - FI status + ceiling
    FI_DOWNGRADE_HISTORY: API_BASE_P3 + '/api/governance/fi-authority/downgrades',  // GET - downgrade history
    FI_THRESHOLDS_CONFIG: API_BASE_P3 + '/api/governance/fi-authority/thresholds',  // PUT - threshold config
    // Intent Capsule HMAC
    CAPSULE_SIGN: API_BASE_P3 + '/api/governance/capsules/sign',            // POST - sign capsule
    CAPSULE_VERIFY: API_BASE_P3 + '/api/governance/capsules/verify',        // POST - verify capsule
    CAPSULE_DRIFT_CHECK: API_BASE_P3 + '/api/governance/capsules/drift-check'  // GET - drift detection
  },

  // ============================================================================
  // P3 ANALYTICS → P3 Gateway (template-p3.yaml /api/analytics/*)
  // Three R's, Outcome Correlation, Calibration, Health, System Intelligence
  // ============================================================================
  ANALYTICS: {
    // Three R's Classification
    CLASSIFICATION: API_BASE_P3 + '/api/analytics/classification',                  // GET - latest per agent
    CLASSIFICATION_HISTORY: API_BASE_P3 + '/api/analytics/classification/history',   // GET - history
    CLASSIFICATION_TRIGGER: API_BASE_P3 + '/api/analytics/classification/trigger',   // POST - trigger classification
    // Outcome Correlation
    CORRELATIONS: API_BASE_P3 + '/api/analytics/correlations',              // GET - correlations
    CORRELATION_RUN: API_BASE_P3 + '/api/analytics/correlations/run',       // POST - compute correlations
    CORRELATION_TRENDS: API_BASE_P3 + '/api/analytics/correlations/trends', // GET - trend series
    // Calibration Monitor
    CALIBRATION_SCORES: API_BASE_P3 + '/api/analytics/calibration/scores',  // GET - latest scores
    CALIBRATION_HISTORY: API_BASE_P3 + '/api/analytics/calibration/history', // GET - score history
    CALIBRATION_RUN: API_BASE_P3 + '/api/analytics/calibration/run',        // POST - run calibration
    // Health Predictor
    HEALTH_PREDICTIONS: API_BASE_P3 + '/api/analytics/health/predictions',  // GET - latest per agent
    HEALTH_SIGNALS: API_BASE_P3 + '/api/analytics/health/signals',          // GET - 8 signals detail
    HEALTH_PREDICTION_RUN: API_BASE_P3 + '/api/analytics/health/run',       // POST - run prediction
    // System Intelligence Synthesis
    INTELLIGENCE_SYNTHESIS: API_BASE_P3 + '/api/analytics/intelligence/synthesis',      // GET - recent synthesis
    INTELLIGENCE_SYNTHESIS_RUN: API_BASE_P3 + '/api/analytics/intelligence/synthesis/run', // POST - run synthesis
    INTELLIGENCE_INSIGHTS: API_BASE_P3 + '/api/analytics/intelligence/insights'          // GET - insights
  },

  // ============================================================================
  // P3 STANDARDS → P3 Gateway (template-p3.yaml /api/standards/*)
  // Invariant Promotion, Load-Bearing Classification
  // ============================================================================
  P3_STANDARDS: {
    // Invariant Promotion Pipeline
    INVARIANT_CANDIDATES: API_BASE_P3 + '/api/standards/invariants/candidates',  // GET - promotion candidates
    INVARIANT_PROMOTE: API_BASE_P3 + '/api/standards/invariants/promote',        // POST - promote candidate
    INVARIANT_HISTORY: API_BASE_P3 + '/api/standards/invariants/history',         // GET - promotion history
    // Load-Bearing Classification
    LOAD_BEARING_ELEMENTS: API_BASE_P3 + '/api/standards/load-bearing/elements',  // GET - classified elements
    LOAD_BEARING_ANALYZE: API_BASE_P3 + '/api/standards/load-bearing/analyze',    // POST - run analysis
    LOAD_BEARING_HISTORY: API_BASE_P3 + '/api/standards/load-bearing/history'     // GET - analysis runs
  },

  // ============================================================================
  // P3 MEMORY → P3 Gateway (template-p3.yaml /api/memory/*, /api/corrections/*)
  // Hybrid Vector Memory, Temporal Context, Correction Harvesting
  // ============================================================================
  MEMORY: {
    // Hybrid Vector Memory Tiering
    SEARCH: API_BASE_P3 + '/api/memory/search',              // POST - semantic search with tier boost
    ENTRIES: API_BASE_P3 + '/api/memory/entries',             // GET - memory entries
    CONSOLIDATE: API_BASE_P3 + '/api/memory/consolidate',    // POST - run tier consolidation
    STATS: API_BASE_P3 + '/api/memory/stats',                // GET - memory stats
    // Temporal Context Orchestration
    TEMPORAL_CONTEXT: API_BASE_P3 + '/api/memory/temporal/context',       // GET - events
    TEMPORAL_PATTERNS: API_BASE_P3 + '/api/memory/temporal/patterns',     // GET - patterns
    TEMPORAL_PREDICTIONS: API_BASE_P3 + '/api/memory/temporal/predictions', // GET - predictions
    // Passive Correction Harvesting
    CORRECTION_HARVEST_RUN: API_BASE_P3 + '/api/corrections/harvest/run',   // POST - run harvest
    CORRECTIONS_GET: API_BASE_P3 + '/api/corrections/harvested',            // GET - harvested corrections
    CORRECTION_HARVEST_STATS: API_BASE_P3 + '/api/corrections/harvest/stats' // GET - harvest stats
  },

  // ============================================================================
  // P3 INFERENCE → P3 Gateway (template-p3.yaml /api/inference/*)
  // Privacy-First Inference Router
  // ============================================================================
  INFERENCE: {
    ROUTE: API_BASE_P3 + '/api/inference/route',                    // POST - route inference request
    PROVIDERS: API_BASE_P3 + '/api/inference/providers',            // GET - list providers
    PROVIDER_CONFIG: API_BASE_P3 + '/api/inference/providers/config', // PUT - update provider config
    ROUTING_HISTORY: API_BASE_P3 + '/api/inference/routing/history', // GET - routing decisions
    PRIVACY_POLICIES: API_BASE_P3 + '/api/inference/privacy-policies' // GET - privacy policies
  }
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Replace path parameters in endpoint URLs
 * Example: buildEndpoint(API_ENDPOINTS.SUPERADMIN.CLIENT_DETAILS, { clientId: '123' })
 */
export function buildEndpoint(endpoint: string, params?: Record<string, string>): string {
  if (!params) return endpoint;

  let result = endpoint;
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`{${key}}`, encodeURIComponent(value));
  });
  return result;
}

/**
 * Get WebSocket URL based on core API URL
 */
export function getWebSocketUrl(): string {
  return API_BASE_CORE.replace(/^http/, 'ws');
}

/**
 * Get Yjs Collaborative Editing WebSocket URL
 */
export function getYjsWebSocketUrl(): string {
  // Dedicated Yjs WebSocket API Gateway (template-websocket.yaml)
  // Falls back to derived URL for development
  return process.env.REACT_APP_YJS_WEBSOCKET_URL || '';
}

// =============================================================================
// GATEWAY EXPORTS (for direct use if needed)
// =============================================================================

export const API_GATEWAYS = {
  CORE: API_BASE_CORE,              // 179 handlers - business operations
  AGENTS: API_BASE_AGENTS,          // 78 handlers - agent operations
  ENHANCEMENTS: API_BASE_ENHANCEMENTS,  // 78 handlers - security, tracing, governance
  P3: API_BASE_P3                   // 48 handlers - analytics, memory, inference, governance hardening
};

// Legacy exports for backward compatibility
// Note: Endpoints already include full URLs, so these return empty string
// to prevent double-prefixing in Make_Authorized_API_Call
export const getApiBaseUrl = () => '';
export const getAgentsApiBaseUrl = () => '';
